/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosRequestConfig } from 'axios';
import { contentType, getStorage } from 'utils';
import {
  API_BASE_URL,
  APP_AUTH_ENABLE,
  APP_TOKEN_NAME,
  ON_FETCH_ERROR,
  ON_PARSE_ERROR,
  ON_RESPONSE_ERROR,
} from './constants';
import HttpRequest from './httpRequest';
import { IApiResponse, IDataRequest, IError, IHeaderRequest, IResponse } from 'types/api';

export enum EMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  HEAD = 'head',
  DELETE = 'delete',
}

export enum EContentType {
  JSON = 'application/json',
  BINARY = 'multipart/form-data',
  TEXT = 'plain/text',
  URLENCODED = 'application/x-www-form-urlencoded',
}

const http = HttpRequest();

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: EContentType.JSON,
    ...contentType(EContentType.JSON),
    mode: 'cors',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': '*',
  },
  withCredentials: false,
});

function execApi<T>(
  method: EMethod,
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
) {
  configs = configs ?? {};
  Object.assign(configs, { url: uri, method, headers, data: null });
  if (data) {
    if (configs.method === EMethod.GET) {
      configs.method = EMethod.POST;
    }
    if (data instanceof FormData) {
      headers = Object.assign(headers ?? {}, contentType(EContentType.BINARY));
      configs.data = data;
    } else {
      configs.data = data;
    }
  }

  Object.assign(configs, { headers: configs.headers || {} });

  APP_AUTH_ENABLE &&
    !configs.headers?.Authorization &&
    Object.assign(configs.headers ?? '', {
      Authorization: 'Bearer ' + localStorage.getItem(APP_TOKEN_NAME),
    });

  return http.request(configs)
    .then((response: any) => {
      const result: IApiResponse<T> = {
        data: null,
        detail: '',
        success: false,
        errors: [],
      };

      try {
        result.success = Math.floor(response.status / 200) === 1;

        if (result.success) {
          result.data = response.data;
          result.success = true;
          result.errors = [];
        } else {
          result.errors = response.data.errors ?? ON_RESPONSE_ERROR;
          try {
            if (response.status === 403) {
              //
            }
            if (response.status !== 204) {
              //
            }
          } catch (e) {
            //
          }
        }
      } catch (e) {
        result.errors = ON_PARSE_ERROR;
        try {
          if (response?.status === 403) {
            //
          }
          if (response?.status !== 204) {
            //
          }
        } catch (e) {
          //
        }
      }
      return result;
    })
    .catch(
      (
        error: { response: { status: number; data: any; code: string } } | any
      ) => {
        try {
          if (error.response && error.response.status === 403) {
            localStorage.clear();
            return {
              detail: error.response.data.message,
            };
          }
          if (error.response && error.response.status !== 204) {
            //
          }
          if (error && error.code === 'ERR_NETWORK') {
            return {
              detail: error.response.data,
            };
          }
          if (
            error.response && (
              error.response.status === 500 ||
              error.response.status === 502 ||
              error.response.status === 504
            )
          ) {
            return {
              detail: error.response.data,
            };
          }
          if (
            error.response && (
              error.response.status === 404
            )
          ) {
            return {
              detail: error.response.data,
            };
          }
        } catch (e) {
          //
        }
        if (error.response && error.response.data) {
          const response = error.response.data;
          response.success = false;
          return response;
        } else {
          return {
            success: false,
            data: null,
            detail: "Cannot send request.",
          };
        }
      }

    );
}

export function apiGet<T>(
  uri: string,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi<T>(EMethod.GET, uri, undefined, headers, configs);
}

export function apiPost<T>(
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi(EMethod.POST, uri, data, headers, configs);
}

export function apiPut<T>(
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi(EMethod.PUT, uri, data, headers, configs);
}

export function apiPatch<T>(
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi(EMethod.PATCH, uri, data, headers, configs);
}

export function apiDelete<T>(
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi(EMethod.DELETE, uri, data, headers, configs);
}

export function apiHead<T>(
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  return execApi(EMethod.HEAD, uri, data, headers, configs);
}

export function api<T>(
  method: EMethod,
  uri: string,
  data?: IDataRequest,
  headers?: IHeaderRequest,
  configs?: AxiosRequestConfig
): IResponse<T> {
  switch (method) {
    case EMethod.POST:
      return apiPost<T>(uri, data, headers, configs);

    case EMethod.PUT:
      return apiPut(uri, data, headers, configs);

    case EMethod.PATCH:
      return apiPatch(uri, data, headers, configs);

    case EMethod.HEAD:
      return apiHead(uri, data, headers, configs);

    case EMethod.DELETE:
      return apiDelete(uri, data, headers, configs);

    default:
      return apiGet<T>(uri, headers, configs);
  }
}

export const parseError = (errors: IError[]) => {
  return errors.map((error) => {
    return {
      ...error,
      loc: error.loc
        .split(' -> ')
        .map((el: any) => (el.match(/^\d+$/) ? +el : el))
        .filter((_, i: any) => i !== 0),
    };
  });
};
